import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  LanguageCode,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "../config/languages";
import { MultilingualString, getLocalizedString } from "../types/multilingual";
import AppConfig from "../../constants";
import i18n from "../config/i18n";

/**
 * 언어 컨텍스트 타입
 */
interface LanguageContextType {
  currentLanguage: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  getLocalizedText: (multilingual: MultilingualString) => string;
  isLanguageSupported: (language: LanguageCode) => boolean;
}

/**
 * 언어 컨텍스트 생성
 */
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

/**
 * 언어 컨텍스트 프로바이더 컴포넌트
 */
interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [currentLanguage, setCurrentLanguage] =
    useState<LanguageCode>(DEFAULT_LANGUAGE);

  // 로컬 스토리지에서 언어 설정 불러오기
  useEffect(() => {
    const savedLanguage = localStorage.getItem(
      AppConfig.KEYS.LANG,
    ) as LanguageCode;
    if (savedLanguage && SUPPORTED_LANGUAGES[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // 언어 변경 시 body에 클래스 추가 (CSS에서 배너 높이 조건부 적용용)
  useEffect(() => {
    document.body.classList.remove("lang-ko", "lang-en");
    document.body.classList.add(`lang-${currentLanguage}`);
  }, [currentLanguage]);

  // 언어 변경 시 로컬 스토리지에 저장하고 i18n도 변경
  const setLanguage = (language: LanguageCode) => {
    setCurrentLanguage(language);
    localStorage.setItem(AppConfig.KEYS.LANG, language);
    i18n.changeLanguage(language);
  };

  // 다국어 문자열에서 현재 언어의 텍스트 가져오기
  const getLocalizedText = (multilingual: MultilingualString): string => {
    return getLocalizedString(multilingual, currentLanguage);
  };

  // 언어 지원 여부 확인
  const isLanguageSupported = (language: LanguageCode): boolean => {
    return language in SUPPORTED_LANGUAGES;
  };

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    getLocalizedText,
    isLanguageSupported,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * 언어 컨텍스트 사용을 위한 훅
 */
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

/**
 * 다국어 문자열에서 현재 언어의 텍스트를 가져오는 편의 훅
 */
export function useLocalizedText() {
  const { getLocalizedText } = useLanguage();
  return getLocalizedText;
}
